import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

export interface VoiceCommand {
  command: string;
  text?: string;
  targetField?: string;
}

export interface VoiceWritingState {
  isListening: boolean;
  isActive: boolean;
  currentField: string | null;
  lastCommand: string | null;
  error: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class VoiceWritingService {
  private recognition: any;
  private isSupported = false;

  // Estado del servicio
  private stateSubject = new BehaviorSubject<VoiceWritingState>({
    isListening: false,
    isActive: false,
    currentField: null,
    lastCommand: null,
    error: null
  });

  // Observables públicos
  public state$ = this.stateSubject.asObservable();
  public commandExecuted$ = new Subject<VoiceCommand>();

  // Configuración de comandos
  private navigationCommands = {
    'ir a detalle': 'observations',
    'ir a tratamiento': 'treatment',
    'ir a observaciones': 'observations'
  };

  private actionCommands = {
    'texto': 'DICTATE_MODE',
    'punto': '.',
    'coma': ',',
    'punto y coma': ';',
    'dos puntos': ':',
    'nueva línea': '\n',
    'salto de línea': '\n',
    'borrar': 'DELETE',
    'limpiar': 'CLEAR',
    'terminar': 'END_SESSION'
  };

  constructor() {
    this.initializeSpeechRecognition();
  }

  private initializeSpeechRecognition(): void {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
      this.recognition = new SpeechRecognition();
      this.isSupported = true;

      // Configuración del reconocimiento
      this.recognition.continuous = true;
      this.recognition.interimResults = true;
      this.recognition.lang = 'es-ES';
      this.recognition.maxAlternatives = 1;

      // Event listeners
      this.recognition.onstart = () => {
        this.updateState({ isListening: true, error: null });
      };

      this.recognition.onend = () => {
        this.updateState({ isListening: false });
        // Reiniciar automáticamente si está activo
        if (this.stateSubject.value.isActive) {
          setTimeout(() => this.startListening(), 100);
        }
      };

      this.recognition.onerror = (event: any) => {
        this.updateState({
          isListening: false,
          error: `Error de reconocimiento: ${event.error}`
        });
      };

      this.recognition.onresult = (event: any) => {
        this.processVoiceResult(event);
      };

    } else {
      this.updateState({
        error: 'Reconocimiento de voz no soportado en este navegador'
      });
    }
  }

  private processVoiceResult(event: any): void {
    const results = event.results;
    const lastResult = results[results.length - 1];

    if (lastResult.isFinal) {
      const transcript = lastResult[0].transcript.toLowerCase().trim();
      this.processCommand(transcript);
    }
  }

  private processCommand(transcript: string): void {
    console.log('Comando recibido:', transcript);

    // Verificar comandos de navegación
    for (const [command, field] of Object.entries(this.navigationCommands)) {
      if (transcript.includes(command)) {
        this.executeNavigationCommand(field);
        return;
      }
    }

    // Verificar comandos de acción
    for (const [command, action] of Object.entries(this.actionCommands)) {
      if (transcript === command) {
        this.executeActionCommand(action);
        return;
      }
    }

    // Si estamos en modo dictado, procesar como texto
    if (this.stateSubject.value.currentField) {
      this.executeDictateCommand(transcript);
    }
  }

  private executeNavigationCommand(targetField: string): void {
    this.updateState({
      currentField: targetField,
      lastCommand: `Navegando a ${targetField}`
    });

    this.commandExecuted$.next({
      command: 'NAVIGATE',
      targetField: targetField
    });
  }

  private executeActionCommand(action: string): void {
    const currentField = this.stateSubject.value.currentField;

    if (!currentField && action !== 'END_SESSION') {
      this.updateState({
        error: 'Debe navegar a un campo primero'
      });
      return;
    }

    switch (action) {
      case 'DICTATE_MODE':
        this.updateState({
          lastCommand: 'Modo dictado activado. Hable su texto...'
        });
        break;

      case 'DELETE':
        this.commandExecuted$.next({
          command: 'DELETE',
          targetField: currentField
        });
        break;

      case 'CLEAR':
        this.commandExecuted$.next({
          command: 'CLEAR',
          targetField: currentField
        });
        break;

      case 'END_SESSION':
        this.stopVoiceWriting();
        break;

      default:
        // Caracteres de puntuación
        this.commandExecuted$.next({
          command: 'APPEND_TEXT',
          text: action,
          targetField: currentField
        });
    }
  }

  private executeDictateCommand(text: string): void {
    const currentField = this.stateSubject.value.currentField;

    if (!currentField) {
      this.updateState({
        error: 'No hay campo seleccionado para dictado'
      });
      return;
    }

    // Limpiar el texto y capitalizarlo apropiadamente
    const cleanText = this.cleanAndFormatText(text);

    this.commandExecuted$.next({
      command: 'APPEND_TEXT',
      text: cleanText,
      targetField: currentField
    });

    this.updateState({
      lastCommand: `Texto dictado: "${cleanText}"`
    });
  }

  private cleanAndFormatText(text: string): string {
    // Remover comandos conocidos del texto
    let cleanText = text;

    // Remover comandos de navegación
    Object.keys(this.navigationCommands).forEach(command => {
      cleanText = cleanText.replace(new RegExp(command, 'gi'), '');
    });

    // Remover comandos de acción
    Object.keys(this.actionCommands).forEach(command => {
      cleanText = cleanText.replace(new RegExp(`\\b${command}\\b`, 'gi'), '');
    });

    // Limpiar espacios múltiples
    cleanText = cleanText.replace(/\s+/g, ' ').trim();

    // Capitalizar primera letra
    if (cleanText.length > 0) {
      cleanText = cleanText.charAt(0).toUpperCase() + cleanText.slice(1);
    }

    return cleanText;
  }

  private updateState(updates: Partial<VoiceWritingState>): void {
    const currentState = this.stateSubject.value;
    this.stateSubject.next({ ...currentState, ...updates });
  }

  private startListening(): void {
    if (this.isSupported && this.recognition) {
      try {
        this.recognition.start();
      } catch (error) {
        console.error('Error al iniciar reconocimiento:', error);
      }
    }
  }

  private stopListening(): void {
    if (this.recognition) {
      this.recognition.stop();
    }
  }

  // Métodos públicos
  public startVoiceWriting(): void {
    if (!this.isSupported) {
      this.updateState({
        error: 'Reconocimiento de voz no soportado'
      });
      return;
    }

    this.updateState({
      isActive: true,
      error: null,
      lastCommand: 'Escritura por voz iniciada. Diga "ir a detalle" o "ir a tratamiento"'
    });

    this.startListening();
  }

  public stopVoiceWriting(): void {
    this.updateState({
      isActive: false,
      currentField: null,
      lastCommand: 'Escritura por voz detenida'
    });

    this.stopListening();
  }

  public isVoiceWritingSupported(): boolean {
    return this.isSupported;
  }

  public getCurrentState(): VoiceWritingState {
    return this.stateSubject.value;
  }
}
